/**
 * GuardiansPanel — Alfred UI for managing a family's ParentGuardian rows.
 *
 * Embedded inside the Parent profile modal. Lists the family's guardians,
 * supports add / edit / delete, and lets an admin promote a guardian to
 * primary outbound (which mirrors the phone to User.phone_number via the
 * backend pre_save signal).
 *
 * Self-contained — no Redux integration. Calls the CRUD client directly.
 */
import {
  Avatar,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import StarIcon from '@material-ui/icons/Star'
import StarBorderIcon from '@material-ui/icons/StarBorder'
import PropTypes from 'prop-types'
import React from 'react'

import {
  createGuardian,
  deleteGuardian,
  listGuardians,
  mergeParents,
  previewParentMerge,
  setPrimaryGuardian,
  updateGuardian
} from 'api'
import CloudFileUploader from 'app/components/CloudFileUploader'
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderAction,
  CardTitle
} from 'app/components/generic/Card'
import { uploadPresets } from 'app/config'
import constants from 'app/constants'
import { eventAlert, showToast } from 'app/helpers'

const RELATIONSHIPS = [
  { value: 'MOTHER', label: 'Mother' },
  { value: 'FATHER', label: 'Father' },
  { value: 'MATERNAL_GRANDMOTHER', label: 'Maternal Grandmother' },
  { value: 'MATERNAL_GRANDFATHER', label: 'Maternal Grandfather' },
  { value: 'PATERNAL_GRANDMOTHER', label: 'Paternal Grandmother' },
  { value: 'PATERNAL_GRANDFATHER', label: 'Paternal Grandfather' },
  { value: 'AUNT', label: 'Aunt' },
  { value: 'UNCLE', label: 'Uncle' },
  { value: 'SIBLING', label: 'Sibling' },
  { value: 'NANNY', label: 'Nanny' },
  { value: 'LEGAL_GUARDIAN', label: 'Legal Guardian' },
  { value: 'OTHER', label: 'Other' }
]

const EMPTY_FORM = {
  phone_number: '',
  first_name: '',
  last_name: '',
  image: '',
  relationship: 'MOTHER',
  is_primary_outbound: false
}

// Optional +91 / 91 country code, then a 10-digit number starting 6-9.
const isIndianMobile = (value) =>
  /^(?:\+?91)?[6-9]\d{9}$/.test((value || '').replace(/\s+/g, ''))

const GuardiansPanel = ({ parentUuid }) => {
  const [guardians, setGuardians] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [editingGuardian, setEditingGuardian] = React.useState(null)
  const [modalOpen, setModalOpen] = React.useState(false)
  const [form, setForm] = React.useState({ ...EMPTY_FORM })
  const [formErrors, setFormErrors] = React.useState({})
  const [submitting, setSubmitting] = React.useState(false)
  // Duplicate-family merge: when the typed phone already belongs to a parent.
  const [mergeOpen, setMergeOpen] = React.useState(false)
  const [mergePreview, setMergePreview] = React.useState(null)
  const [mergeSubmitting, setMergeSubmitting] = React.useState(false)

  const refresh = React.useCallback(async () => {
    if (!parentUuid) return
    setLoading(true)
    try {
      const data = await listGuardians(parentUuid)
      setGuardians(data?.results || data || [])
    } catch (error) {
      eventAlert(error?.message || 'Failed to load guardians', constants.ERROR)
    } finally {
      setLoading(false)
    }
  }, [parentUuid])

  React.useEffect(() => {
    refresh()
  }, [refresh])

  const openAdd = () => {
    setEditingGuardian(null)
    setForm({ ...EMPTY_FORM })
    setFormErrors({})
    setModalOpen(true)
  }

  const openEdit = (guardian) => {
    setEditingGuardian(guardian)
    setForm({
      phone_number: guardian.phone_number || '',
      first_name: guardian.first_name || '',
      last_name: guardian.last_name || '',
      image: guardian.image || '',
      relationship: guardian.relationship || 'OTHER',
      is_primary_outbound: !!guardian.is_primary_outbound
    })
    setFormErrors({})
    setModalOpen(true)
  }

  const closeModal = () => {
    if (submitting) return
    setModalOpen(false)
    setEditingGuardian(null)
    setForm({ ...EMPTY_FORM })
    setFormErrors({})
  }

  const validate = () => {
    const errors = {}
    if (!form.phone_number) errors.phone_number = 'Phone is required'
    else if (!isIndianMobile(form.phone_number))
      errors.phone_number =
        'Enter a valid 10-digit Indian mobile (+91 prefix optional)'
    if (!form.relationship) errors.relationship = 'Relationship is required'
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return
    setSubmitting(true)
    const payload = {
      parent: parentUuid,
      phone_number: form.phone_number,
      first_name: form.first_name,
      last_name: form.last_name,
      image: form.image,
      relationship: form.relationship,
      is_primary_outbound: form.is_primary_outbound
    }
    try {
      if (editingGuardian) {
        await updateGuardian(editingGuardian.uuid, payload)
        showToast('Guardian updated', constants.SUCCESS)
        resetForm()
        await refresh()
        return
      }

      // Add mode: if this phone already belongs to a parent, offer to merge
      // that (duplicate) family into this one instead of erroring out.
      const preview = await previewParentMerge({
        sourcePhone: form.phone_number,
        targetParent: parentUuid
      })
      if (preview && preview.is_existing_parent) {
        if (!preview.mergeable) {
          eventAlert(
            (preview.block_reasons || []).join(' ') ||
              'This number belongs to another family that cannot be merged right now.',
            constants.ERROR
          )
          return
        }
        // Keep the add form values; the merge dialog reuses name/relationship.
        setMergePreview(preview)
        setMergeOpen(true)
        return
      }

      await createGuardian(payload)
      showToast('Guardian added', constants.SUCCESS)
      resetForm()
      await refresh()
    } catch (error) {
      const msg =
        error?.response?.data?.phone_number?.[0] ||
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to save guardian'
      eventAlert(msg, constants.ERROR)
    } finally {
      setSubmitting(false)
    }
  }

  const resetForm = () => {
    setModalOpen(false)
    setEditingGuardian(null)
    setForm({ ...EMPTY_FORM })
  }

  const handleConfirmMerge = async () => {
    if (!mergePreview) return
    setMergeSubmitting(true)
    try {
      await mergeParents({
        sourceParent: mergePreview.source.parent_uuid,
        targetParent: parentUuid,
        guardian: {
          first_name: form.first_name,
          last_name: form.last_name,
          image: form.image,
          relationship: form.relationship
        }
      })
      showToast('Family merged and guardian added', constants.SUCCESS)
      setMergeOpen(false)
      setMergePreview(null)
      resetForm()
      await refresh()
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        'Failed to merge family'
      eventAlert(msg, constants.ERROR)
    } finally {
      setMergeSubmitting(false)
    }
  }

  const handleSetPrimary = async (guardian) => {
    try {
      await setPrimaryGuardian(guardian.uuid)
      showToast(
        `${guardian.first_name || 'Guardian'} is now primary`,
        constants.SUCCESS
      )
      await refresh()
    } catch (error) {
      eventAlert(error?.message || 'Failed to set primary', constants.ERROR)
    }
  }

  const handleDelete = async (guardian) => {
    // eslint-disable-next-line no-alert
    if (
      !window.confirm(`Remove ${guardian.first_name || guardian.phone_number}?`)
    ) {
      return
    }
    try {
      await deleteGuardian(guardian.uuid)
      showToast('Guardian removed', constants.SUCCESS)
      await refresh()
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to remove guardian'
      eventAlert(msg, constants.ERROR)
    }
  }

  return (
    <Card>
      <CardHeader tone="blue">
        <CardTitle>Guardians</CardTitle>
        <CardHeaderAction>
          <Button
            variant="contained"
            size="small"
            onClick={openAdd}
            data-testid="add-guardian-btn"
          >
            + Add Guardian
          </Button>
        </CardHeaderAction>
      </CardHeader>
      <CardBody>
        {loading ? (
          <CircularProgress size={20} />
        ) : guardians.length === 0 ? (
          <div style={{ color: '#888', fontSize: 14 }}>
            No guardians on file. Add one to enable multi-phone login for this
            family.
          </div>
        ) : (
          guardians.map((g) => (
            <GuardianRow
              key={g.uuid}
              guardian={g}
              onEdit={openEdit}
              onDelete={handleDelete}
              onSetPrimary={handleSetPrimary}
            />
          ))
        )}
      </CardBody>

      <Dialog open={modalOpen} onClose={closeModal} fullWidth maxWidth="sm">
        <DialogTitle>
          {editingGuardian ? 'Edit Guardian' : 'Add Guardian'}
        </DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <TextField
              label="Phone number"
              value={form.phone_number}
              onChange={(e) => {
                // Capture the value synchronously: React 16 pools synthetic
                // events, so e.target is nulled before the functional setState
                // updater runs (would throw "Cannot read 'value' of null").
                const { value } = e.target
                setForm((f) => ({ ...f, phone_number: value }))
              }}
              error={!!formErrors.phone_number}
              helperText={formErrors.phone_number || 'e.g. +919876543210'}
              fullWidth
              autoFocus
              data-testid="phone-input"
            />
            <TextField
              label="First name"
              value={form.first_name}
              onChange={(e) => {
                const { value } = e.target
                setForm((f) => ({ ...f, first_name: value }))
              }}
              fullWidth
            />
            <TextField
              label="Last name"
              value={form.last_name}
              onChange={(e) => {
                const { value } = e.target
                setForm((f) => ({ ...f, last_name: value }))
              }}
              fullWidth
            />
            <TextField
              label="Relationship to child"
              select
              value={form.relationship}
              onChange={(e) => {
                const { value } = e.target
                setForm((f) => ({ ...f, relationship: value }))
              }}
              error={!!formErrors.relationship}
              helperText={formErrors.relationship}
              fullWidth
            >
              {RELATIONSHIPS.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </TextField>
            <div>
              <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>
                Profile picture (optional)
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {form.image ? (
                  <img
                    src={form.image}
                    alt="guardian avatar"
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      objectFit: 'cover'
                    }}
                  />
                ) : null}
                <CloudFileUploader
                  name="image"
                  label="photo"
                  uploadedFileUrl={form.image || ''}
                  uploadPreset={uploadPresets[constants.USER]}
                  fileUrlOnly
                  onUpload={({ data }) =>
                    setForm((f) => ({ ...f, image: data?.secure_url || '' }))
                  }
                  setFieldValue={(name, value) =>
                    setForm((f) => ({ ...f, image: value }))
                  }
                />
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={submitting}
            data-testid="save-guardian-btn"
          >
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={mergeOpen}
        onClose={() => !mergeSubmitting && setMergeOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Merge existing family?</DialogTitle>
        <DialogContent>
          {mergePreview && (
            <div style={{ fontSize: 14, lineHeight: 1.6 }}>
              <p>
                <b>{mergePreview.source?.name}</b> (
                {mergePreview.source?.phone_number}) is already registered as a
                separate parent account. Merging moves all of their data into
                this family and keeps their number as a guardian here.
              </p>
              <p style={{ margin: '8px 0 4px', color: '#444' }}>
                This will move:
              </p>
              <ul style={{ margin: 0, paddingLeft: 18, color: '#444' }}>
                <li>{mergePreview.counts?.students} student(s)</li>
                <li>{mergePreview.counts?.active_guardians} guardian(s)</li>
                <li>{mergePreview.counts?.coin_balance} coins</li>
                <li>
                  {mergePreview.counts?.subscriptions_total} subscription(s),{' '}
                  {mergePreview.counts?.transactions} payment(s),{' '}
                  {mergePreview.counts?.invoices} invoice(s)
                </li>
              </ul>
              <p style={{ marginTop: 12, color: '#b71c1c', fontWeight: 600 }}>
                This cannot be automatically undone — the other account will be
                closed.
              </p>
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>
                  Guardian profile picture (optional)
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {form.image ? (
                    <img
                      src={form.image}
                      alt="guardian avatar"
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : null}
                  <CloudFileUploader
                    name="image"
                    label="photo"
                    uploadedFileUrl={form.image || ''}
                    uploadPreset={uploadPresets[constants.USER]}
                    fileUrlOnly
                    onUpload={({ data }) =>
                      setForm((f) => ({ ...f, image: data?.secure_url || '' }))
                    }
                    setFieldValue={(name, value) =>
                      setForm((f) => ({ ...f, image: value }))
                    }
                  />
                </div>
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setMergeOpen(false)}
            disabled={mergeSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmMerge}
            color="primary"
            variant="contained"
            disabled={mergeSubmitting}
            data-testid="confirm-merge-btn"
          >
            {mergeSubmitting ? 'Merging…' : 'Merge family'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}

GuardiansPanel.propTypes = {
  parentUuid: PropTypes.string
}

const GuardianRow = ({ guardian, onEdit, onDelete, onSetPrimary }) => {
  const name =
    `${guardian.first_name || ''} ${guardian.last_name || ''}`.trim() ||
    'Unnamed'
  const relationshipLabel =
    RELATIONSHIPS.find((r) => r.value === guardian.relationship)?.label ||
    guardian.relationship

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 4px',
        borderBottom: '1px solid #f0f0f0'
      }}
      data-testid={`guardian-row-${guardian.uuid}`}
    >
      <Avatar
        src={guardian.image || undefined}
        style={{ width: 36, height: 36 }}
      >
        {(guardian.first_name || guardian.phone_number || '?')
          .charAt(0)
          .toUpperCase()}
      </Avatar>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>{name}</div>
        <div style={{ color: '#666', fontSize: 13 }}>
          {guardian.phone_number} · {relationshipLabel}
          {guardian.is_primary_outbound ? (
            <span style={{ color: '#1976d2', fontWeight: 600 }}>
              {' '}
              · ★ Primary outbound
            </span>
          ) : null}
        </div>
      </div>
      <IconButton
        size="small"
        onClick={() => onSetPrimary(guardian)}
        title={
          guardian.is_primary_outbound
            ? 'Already primary'
            : 'Set as primary outbound'
        }
        disabled={!!guardian.is_primary_outbound}
        data-testid={`primary-btn-${guardian.uuid}`}
      >
        {guardian.is_primary_outbound ? <StarIcon /> : <StarBorderIcon />}
      </IconButton>
      <IconButton size="small" onClick={() => onEdit(guardian)} title="Edit">
        <EditIcon />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => onDelete(guardian)}
        title="Remove"
      >
        <DeleteIcon />
      </IconButton>
    </div>
  )
}

GuardianRow.propTypes = {
  guardian: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSetPrimary: PropTypes.func.isRequired
}

export { GuardiansPanel }
export default GuardiansPanel
